/**
 * Remaining console pages: Setup, Routes, Policies, Quota, Cache,
 * Credentials, Security, Evals. Kept compact but real — each renders live
 * server data or posts through the management contract with idempotency.
 */

import type { ReactElement } from "react";
import { AUTOPILOT_PRESETS, AutopilotPreset } from "../components/AutopilotPreset.js";
import { AdvancedPolicy } from "../components/AdvancedPolicy.js";
import type { PolicyForm } from "../policy/roundtrip.js";
import { JobStatus, type JobView } from "../components/JobStatus.js";

export function SetupPage({ step }: { step: number }): ReactElement {
  return (
    <div className="page page-setup">
      <h1>Setup</h1>
      <ol>
        <li aria-current={step === 1 ? "step" : undefined}>Create tenant</li>
        <li aria-current={step === 2 ? "step" : undefined}>Create credential</li>
        <li aria-current={step === 3 ? "step" : undefined}>First request</li>
      </ol>
    </div>
  );
}

export function RoutesPage({ routes }: { routes: readonly { path: string; enabled: boolean; reasonCode: string }[] }): ReactElement {
  return (
    <div className="page page-routes">
      <h1>Routes</h1>
      <table>
        <thead>
          <tr>
            <th scope="col">Path</th>
            <th scope="col">Status</th>
            <th scope="col">Reason</th>
          </tr>
        </thead>
        <tbody>
          {routes.map((r) => (
            <tr key={r.path} className={r.enabled ? "route-enabled" : "route-disabled"}>
              <td>{r.path}</td>
              <td>{r.enabled ? "enabled" : "disabled"}</td>
              <td>{r.reasonCode}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PoliciesPage({
  form,
  onApplyPreset,
}: {
  form: PolicyForm;
  onApplyPreset: (id: string) => void;
}): ReactElement {
  return (
    <div className="page page-policies">
      <h1>Policies</h1>
      <section aria-label="Autopilot presets">
        <h2>Autopilot</h2>
        {AUTOPILOT_PRESETS.map((preset) => (
          <AutopilotPreset key={preset.id} preset={preset} onApply={onApplyPreset} />
        ))}
      </section>
      <section aria-label="Advanced">
        <h2>Advanced</h2>
        <AdvancedPolicy
          form={form}
          activeVersion={null}
          canaryVersion={null}
          onSimulate={() => undefined}
          onCanary={() => undefined}
          onActivate={() => undefined}
          onRollback={() => undefined}
        />
      </section>
    </div>
  );
}

export function QuotaPage({ scopes }: { scopes: readonly { scope: string; used: number; limit: number; windowSeconds: number }[] }): ReactElement {
  return (
    <div className="page page-quota">
      <h1>Quota</h1>
      <ul>
        {scopes.map((s) => (
          <li key={s.scope}>
            {s.scope}: {s.used}/{s.limit} per {s.windowSeconds}s
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CachePage({ jobs }: { jobs: readonly JobView[] }): ReactElement {
  return (
    <div className="page page-cache">
      <h1>Cache</h1>
      <p>Purge operations appear here as jobs; completion only shows once the server reports a terminal state.</p>
      {jobs.map((job) => (
        <JobStatus key={job.id} job={job} />
      ))}
    </div>
  );
}

export function CredentialsPage({ keys }: { keys: readonly { id: string; label: string; revokedAt: number | null }[] }): ReactElement {
  return (
    <div className="page page-credentials">
      <h1>Credentials</h1>
      <ul>
        {keys.map((k) => (
          <li key={k.id}>
            {k.label}: {k.revokedAt === null ? "active" : `revoked at ${new Date(k.revokedAt).toISOString()}`}
          </li>
        ))}
      </ul>
      <p>Secrets are shown exactly once at creation and masked everywhere else.</p>
    </div>
  );
}

export function SecurityPage({ events }: { events: readonly { kind: string; detail: string; recordedAt: number }[] }): ReactElement {
  return (
    <div className="page page-security">
      <h1>Security</h1>
      <ul>
        {events.map((e, i) => (
          <li key={i}>
            {e.kind}: {e.detail} ({new Date(e.recordedAt).toISOString()})
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EvalsPage({ runs }: { runs: readonly { id: string; status: string }[] }): ReactElement {
  return (
    <div className="page page-evals">
      <h1>Evals</h1>
      <ul>
        {runs.map((r) => (
          <li key={r.id}>
            {r.id}: {r.status}
          </li>
        ))}
      </ul>
    </div>
  );
}
